import {Component, computed, input, output} from '@angular/core';

/**
 * The **slot arrangement control** of the Artikel create/edit slide-over: a pair of up/down buttons that move
 * one "Bestandteil" within its article's list.
 *
 * The list order on that screen IS the arrangement — it is stamped onto
 * `CompositionComponentLinker.Order` when the article is saved and is what the register's
 * "Bestandteile" wizard, the modification lines on a receipt and every other consumer render in
 * (Bug #728). Before #728 the only way to change it was to delete a slot and re-add it at the end.
 *
 * Presentational: it holds no list and mutates nothing, it emits {@link move} with the direction and lets the
 * parent reorder its own array. See `docs/articles/inventory/items-management.md`.
 */
@Component({
  selector: 'app-composition-slot-reorder',
  standalone: true,
  templateUrl: './composition-slot-reorder.component.html'
})
export class CompositionSlotReorderComponent {
  /** Zero-based position of this slot in the article's list. */
  readonly index = input.required<number>();
  /** How many slots the article has in total. */
  readonly count = input.required<number>();
  /**
   * Human label of the slot, used to build the buttons' accessible names — a screen-reader user hears
   * "Bestandteil 2 nach oben", not two unlabelled buttons repeated once per row.
   */
  readonly label = input<string>('Bestandteil');
  /** Emitted with the requested direction; the parent performs the swap. */
  readonly move = output<'up' | 'down'>();

  /** The first slot cannot move further up. */
  protected readonly isFirst = computed(() => this.index() <= 0);
  /** The last slot cannot move further down. */
  protected readonly isLast = computed(() => this.index() >= this.count() - 1);

  /** Accessible name for the "up" button, e.g. `Bestandteil 2 nach oben verschieben`. */
  protected readonly upLabel = computed(() => `${this.label()} ${this.index() + 1} nach oben verschieben`);
  /** Accessible name for the "down" button. */
  protected readonly downLabel = computed(() => `${this.label()} ${this.index() + 1} nach unten verschieben`);
}
